import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { identifyUser, resetUser } from "../analytics/posthog";

const AuthContext = createContext(null);

const TOKEN_KEY = "access_token";

function parseJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const login = useCallback((accessToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    setToken(accessToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    resetUser();
  }, []);

  const email = useMemo(() => {
    if (!token) return null;
    const payload = parseJwtPayload(token);
    return payload?.email || null;
  }, [token]);

  const role = useMemo(() => {
    if (!token) return null;
    const payload = parseJwtPayload(token);
    return payload?.role || "user";
  }, [token]);

  const isAuthenticated = useMemo(() => {
    if (!token) return false;
    const payload = parseJwtPayload(token);
    if (!payload?.exp) return false;
    return payload.exp * 1000 > Date.now();
  }, [token]);

  const userId = useMemo(() => {
    if (!token) return null;
    const payload = parseJwtPayload(token);
    return payload?.sub || null;
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      identifyUser(userId, { email, role });
    }
  }, [isAuthenticated, userId, email, role]);

  const value = useMemo(
    () => ({ token, isAuthenticated, email, role, login, logout }),
    [token, isAuthenticated, email, role, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
